/** Displays the toolbar for formatting text, used by TextEditor */
import React, { useState } from "react";

//imports all the material-ui icons
import IconButton from "@mui/material/Button";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { Popover, styled } from "@mui/material";
import {
    StyledEditorToolbar,
    FormatBoldIcon,
    FormatItalicIcon,
    FormatUnderlinedIcon,
    StrikethroughSIcon,
    HighlightIcon,
    FormatSizeIcon,
    FormatListBulletedIcon,
    FormatListNumberedIcon,
    LinkIcon,
    ImageIcon,
    CodeIcon,
    IntegrationInstructionsIcon,
} from "./styled";
import { ToolbarProps } from "./types";
import EditorButton from "./EditorButton";
import clsx from "clsx";
import { TwitterPicker } from "react-color";

export const StyledTwitterPicker = styled(TwitterPicker)(({ theme }) => {
    return {
        ".root": {
            margin: theme.spacing(0.5),
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: "#0f0 !important",
        },
        ".label": {
            textTransform: "none",
        },
    };
});

/**
 * Renders Twitter-style color picker
 * @param {TwitterPickerProps} props Determines appearance and color swatches of picker
 * @returns TwitterPicker
 */
export const TwitterColorPicker = ({
    color,
    onChangeComplete,
    width,
    colors,
    triangle,
}: any) => {
    return (
        <StyledTwitterPicker
            className={clsx("root")}
            color={color}
            onChangeComplete={onChangeComplete}
            width={width}
            colors={colors}
            triangle={triangle}
        />
    );
};

/**
 * Returns standard NoteTab toolbar
 * @param {GridJustification} align align property of buttons in toolbar
 * @returns {jsx} toolbar
 */
export const NoteTabToolbar: React.FC<ToolbarProps> = ({ align }): any => {
    /** Tracks the currently selected color */
    const [color, setColor] = useState("red");
    /** Location for the color selection popout to appear */
    const [anchorEl, setAnchorEl] = useState(null);

    /**
     * pick and set color for color change button
     * @param {any} color color to change text to
     */
    const onChangeComplete = (color: any) => {
        setColor(color.hex);
    };

    /**
     * Opens popover on color picker button click
     * @param {event} event click event, used to place popover
     */
    const onColorPickerClick = (event: any) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Closes popover (by clicking outside popover)
     */
    const onColorPickerClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "color-picker" : undefined;

    /** Code for all the buttons in the Toolbar for NoteTab*/
    const NoteTabButtons = [
        <EditorButton
            formatting="bold"
            formatType="mark"
            formatIcon={<FormatBoldIcon />}
        />,
        <EditorButton
            formatting="italic"
            formatType="mark"
            formatIcon={<FormatItalicIcon />}
        />,
        <EditorButton
            formatting="underline"
            formatType="mark"
            formatIcon={<FormatUnderlinedIcon />}
        />,
        <EditorButton
            formatting="strikethrough"
            formatType="mark"
            formatIcon={<StrikethroughSIcon />}
        />,
        <EditorButton
            formatting="code"
            formatType="mark"
            formatIcon={<CodeIcon />}
        />,
        <EditorButton
            formatting="codeBlock"
            formatType="mark"
            formatIcon={<IntegrationInstructionsIcon />}
        />,
        <EditorButton
            formatting="link"
            formatType="link"
            formatIcon={<LinkIcon />}
        />,
        /* TODO: Will be need for Post MVP
        <EditorButton
            formatting="image"
            formatType="image"
            formatIcon={<ImageIcon />}
        />, */
        <EditorButton
            formatting={color}
            formatType="color"
            formatIcon={<FormatColorTextIcon style={{ color: color }} />}
        />,
        <EditorButton
            formatting="highlight"
            formatType="mark"
            formatIcon={<HighlightIcon />}
        />,
        <EditorButton
            formatting="heading-two"
            formatType="block"
            formatIcon={<FormatSizeIcon />}
        />,
        <EditorButton
            formatting="numbered-list"
            formatType="block"
            formatIcon={<FormatListNumberedIcon />}
        />,
        <EditorButton
            formatting="bulleted-list"
            formatType="block"
            formatIcon={<FormatListBulletedIcon />}
        />,
        <IconButton
            style={{ color: color }}
            size="small"
            onClick={(e) => onColorPickerClick(e)}
        >
            <ColorLensIcon />
        </IconButton>,
    ];

    const NoteTabPopOver = (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            onClose={onColorPickerClose}
        >
            <TwitterColorPicker
                color={color}
                onChangeComplete={onChangeComplete}
                width="205px"
                colors={["#FF0000", "#FF6600", "#00CC99", "#0099CC", "#9900FF"]}
                triangle="top-right"
            />
        </Popover>
    );

    return (
        <StyledEditorToolbar
            className={clsx("toolbar")}
            buttons={NoteTabButtons}
            popover={NoteTabPopOver}
            align={align}
        />
    );
};
